import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PaginatedResponse } from '@/types/api.types';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Location response type
interface ApiLocation {
  locationId: number;
  name: string;
  description: string;
  type: string | null;
  isActive: boolean;
  entity: string;
  clientId: number | null;
  parentLocationId?: number | null;
  assetCount: number;
}

export interface Location {
  id: number;
  code: string;
  name: string;
  type: string | null; // BACKOFFICE | CLIENT | TRANSFER (Toca only)
  address: string | null;
  isActive: boolean;
  assetCount: number;
  createdAt: string;
  /** F1 — hierarchical networks */
  parentLocationId: number | null;
  parentName: string | null;
  depth: number;
}

export interface LocationSearchParams {
  search?: string;
  type?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

// Query keys
export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (params: LocationSearchParams) => [...locationKeys.lists(), params] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: number) => [...locationKeys.details(), id] as const,
  dropdown: () => [...locationKeys.all, 'dropdown'] as const,
};

// Transform API location to our Location interface
function transformLocation(apiLoc: ApiLocation): Location {
  return {
    id: apiLoc.locationId,
    code: String(apiLoc.locationId),
    name: apiLoc.name || apiLoc.description,
    type: apiLoc.type,
    address: null,
    isActive: apiLoc.isActive,
    assetCount: apiLoc.assetCount ?? 0,
    createdAt: '',
    parentLocationId: apiLoc.parentLocationId ?? null,
    parentName: null,
    depth: 0,
  };
}

/**
 * F1 — annotate parentName + depth and (when treeSort) return the list in
 * depth-first tree order so the table renders as an indented hierarchy.
 * Cycle/orphan tolerant: nodes whose parent isn't in the set act as roots.
 */
export function annotateTree(locations: Location[], treeSort: boolean): Location[] {
  const byId = new Map(locations.map((l) => [l.id, l]));
  for (const l of locations) {
    l.parentName = l.parentLocationId ? byId.get(l.parentLocationId)?.name ?? null : null;
  }
  if (!treeSort) return locations;

  const childrenOf = new Map<number | 0, Location[]>();
  for (const l of locations) {
    const key = l.parentLocationId && byId.has(l.parentLocationId) ? l.parentLocationId : 0;
    const list = childrenOf.get(key) ?? [];
    list.push(l);
    childrenOf.set(key, list);
  }

  const result: Location[] = [];
  const visited = new Set<number>();
  const visit = (parentKey: number | 0, depth: number) => {
    const children = (childrenOf.get(parentKey) ?? []).sort((a, b) => a.name.localeCompare(b.name));
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      child.depth = depth;
      result.push(child);
      visit(child.id, depth + 1);
    }
  };
  visit(0, 0);
  // Safety net for data cycles: append anything the walk never reached.
  for (const l of locations) if (!visited.has(l.id)) result.push(l);
  return result;
}

/**
 * F1 — ids that cannot be chosen as parent of `locationId` (itself and all
 * of its descendants). Used to filter the parent dropdown.
 */
export function invalidParentIds(locations: Location[], locationId: number): Set<number> {
  const excluded = new Set<number>([locationId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const l of locations) {
      if (l.parentLocationId && excluded.has(l.parentLocationId) && !excluded.has(l.id)) {
        excluded.add(l.id);
        grew = true;
      }
    }
  }
  return excluded;
}

// Search locations (uses list endpoint with client-side filtering)
export function useLocations(params: LocationSearchParams) {
  return useQuery({
    queryKey: locationKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiLocation[]>>('/api/v2/location/list');
      // F1 — tree order (with depth indentation) when the list is unfiltered;
      // filtered views fall back to a flat list but keep parentName.
      const hasFilters = !!params.search || !!params.type || params.isActive !== undefined;
      const locations = annotateTree(response.data.data.map(transformLocation), !hasFilters);

      // Client-side filtering since API returns all locations
      let filtered = locations;
      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(l =>
          l.name?.toLowerCase().includes(search) ||
          l.code?.toLowerCase().includes(search)
        );
      }
      if (params.type) {
        filtered = filtered.filter(l => l.type === params.type);
      }
      if (params.isActive !== undefined) {
        filtered = filtered.filter(l => l.isActive === params.isActive);
      }

      // Apply pagination
      const page = params.page || 1;
      const pageSize = params.pageSize || 25;
      const startIndex = (page - 1) * pageSize;
      const paginatedItems = filtered.slice(startIndex, startIndex + pageSize);

      return {
        items: paginatedItems,
        totalCount: filtered.length,
        totalPages: Math.ceil(filtered.length / pageSize),
        page,
        pageSize,
      } as PaginatedResponse<Location>;
    },
  });
}

// Get all locations for dropdown
export function useLocationsDropdown() {
  return useQuery({
    queryKey: locationKeys.dropdown(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiLocation[]>>('/api/v2/location/list');
      return response.data.data.map(transformLocation);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Get single location
export function useLocation(id: number, enabled = true) {
  return useQuery({
    queryKey: locationKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiLocation>>('/api/v2/location/get', {
        params: { locationId: id }
      });
      return transformLocation(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Create location
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { code: string; name: string; type?: string; address?: string; parentLocationId?: number | null }) => {
      const response = await api.post<ApiResponse<number>>('/api/v2/location/save', {
        locationId: 0, // 0 indicates new location
        name: data.name,
        description: data.name,
        type: data.type,
        isActive: true,
        parentLocationId: data.parentLocationId ?? null,
      });
      return response.data.data; // Returns the new locationId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.dropdown() });
    },
  });
}

// Update location
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Location> }) => {
      const response = await api.post<ApiResponse<number>>('/api/v2/location/save', {
        locationId: id,
        name: data.name,
        description: data.name,
        type: data.type,
        isActive: data.isActive ?? true,
        parentLocationId: data.parentLocationId ?? null,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.dropdown() });
    },
  });
}

// Delete location
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.post<ApiResponse<unknown>>('/api/v2/location/delete', { locationId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.dropdown() });
    },
  });
}
